import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { clsx } from "clsx";

import { useUser } from "@/entities/Auth";
import { useAppSelector } from "@/shared/lib/hooks/redux";
import { Spinner } from "@/shared/ui/Spinner";

import WhiteCheckIcon from "@/shared/assets/icons/whiteCheck.svg";

import { ProductInterval } from "../../const/subscription";
import { calcAnnualSavings } from "../../lib/calcAnnualSavings";
import { getBaseProductName } from "../../lib/getBaseProductName";
import { getProductsAreLoading, getProductsList } from "../../model/selectors/subscriptionSelectors";

import cls from "./ProductSelection.module.scss";

type ProductSelectionProps = {
  className?: string;
  variant?: "dark" | "light";
  stripePriceId: string;
  onChange: (stripePriceId: string | null) => void;
  showOnlyHigherPlans?: boolean;
};

export const ProductSelection = (props: ProductSelectionProps) => {
  const { className, variant = "dark", stripePriceId, onChange, showOnlyHigherPlans } = props;

  const user = useUser();
  const productsAreLoading = useAppSelector(getProductsAreLoading);
  const productsList = useAppSelector(getProductsList);

  const previousStripePriceIdRef = useRef<string | null>(null);

  // Annual users should only see higher annual tiers when upgrading;
  // showing monthly plans would allow them to downgrade.
  const filterByProductInterval = showOnlyHigherPlans && user.isProductAnnual;

  const [selectedInterval, setSelectedInterval] = useState<ProductInterval>(
    filterByProductInterval ? ProductInterval.Yearly : ProductInterval.Monthly
  );

  const selectedProduct = useMemo(() => {
    if (!stripePriceId) {
      return null;
    }
    return productsList.find((p) => p.stripePriceId === stripePriceId);
  }, [productsList, stripePriceId]);

  useEffect(() => {
    if (selectedProduct && previousStripePriceIdRef.current !== stripePriceId) {
      setSelectedInterval(selectedProduct.interval);
      previousStripePriceIdRef.current = stripePriceId;
    }
  }, [selectedProduct, stripePriceId]);

  const sortedList = useMemo(() => {
    return [...productsList].sort((a, b) => {
      if (Number(a.price) < Number(b.price)) {
        return -1;
      } else if (Number(a.price) > Number(b.price)) {
        return 1;
      }
      return 0;
    });
  }, [productsList]);

  const currentTierIndex = useMemo(() => {
    const product = user?.product;
    if (!showOnlyHigherPlans || !product) {
      return -1;
    }
    return sortedList
      .filter((p) => p.interval === product.interval)
      .findIndex((p) => p.stripePriceId === product.stripePriceId);
  }, [showOnlyHigherPlans, user?.product, sortedList]);

  const getMonthlyAnalog = (yearlyProductName: string) => {
    const baseName = getBaseProductName(yearlyProductName);

    return sortedList.find((productData) => {
      return productData.interval === ProductInterval.Monthly && getBaseProductName(productData.name) === baseName;
    });
  };

  const getVisibleProducts = useCallback(
    (interval: ProductInterval) => {
      return sortedList
        .filter((productData) => productData.interval === interval)
        .filter(
          (_, index) =>
            !showOnlyHigherPlans ||
            (filterByProductInterval
              ? interval === ProductInterval.Yearly && index > currentTierIndex
              : index > currentTierIndex)
        );
    },
    [sortedList, showOnlyHigherPlans, filterByProductInterval, currentTierIndex]
  );

  const monthlyProducts = useMemo(() => getVisibleProducts(ProductInterval.Monthly), [getVisibleProducts]);

  const yearlyProducts = useMemo(() => getVisibleProducts(ProductInterval.Yearly), [getVisibleProducts]);

  const visibleProducts = useMemo(() => {
    return selectedInterval === ProductInterval.Monthly ? monthlyProducts : yearlyProducts;
  }, [selectedInterval, monthlyProducts, yearlyProducts]);

  const shouldShowPlanSelection = useMemo(() => {
    return monthlyProducts.length > 0 && yearlyProducts.length > 0;
  }, [monthlyProducts.length, yearlyProducts.length]);

  const switchInterval = useCallback(
    (value: ProductInterval) => {
      setSelectedInterval(value);

      if (!selectedProduct) {
        onChange(null);
        return;
      }

      const targetProducts = getVisibleProducts(value);
      const selectedBaseName = getBaseProductName(selectedProduct.name);

      const analogProduct = targetProducts.find(
        (productData) => getBaseProductName(productData.name) === selectedBaseName
      );

      onChange(analogProduct?.stripePriceId ?? null);
    },
    [getVisibleProducts, onChange, selectedProduct]
  );

  return (
    <div className={clsx(cls.Container, cls[variant], className)}>
      {productsAreLoading ? (
        <Spinner />
      ) : (
        <>
          {shouldShowPlanSelection && (
            <div className={clsx(cls.PlanSelection, selectedInterval === ProductInterval.Yearly && cls.bgReverted)}>
              <div
                className={clsx(cls.Btn, selectedInterval === ProductInterval.Monthly && cls.isSelected)}
                onClick={() => switchInterval(ProductInterval.Monthly)}
              >
                Monthly
              </div>
              <div
                className={clsx(cls.Btn, selectedInterval === ProductInterval.Yearly && cls.isSelected)}
                onClick={() => switchInterval(ProductInterval.Yearly)}
              >
                Yearly
                <span className={cls.SaveLabel}>Save up to $180</span>
              </div>
            </div>
          )}

          <div className={cls.ProductSelection}>
            {visibleProducts.map((productData, i) => {
              const isSelected = stripePriceId === productData.stripePriceId;
              const monthlyAnalog = getMonthlyAnalog(productData.name);

              return (
                <div
                  key={i}
                  className={clsx(cls.Product, isSelected && cls.isSelected)}
                  onClick={() => onChange(productData.stripePriceId)}
                >
                  <div className={cls.Name}>{productData.name ?? "None"}</div>
                  <p>Unlimited readings</p>
                  <p>
                    {productData.maxPersonalityChanges === 999 ? "Unlimited" : productData.maxPersonalityChanges}{" "}
                    {productData.maxPersonalityChanges === 1 ? "psychic" : "psychics"}
                  </p>
                  {selectedInterval === ProductInterval.Yearly && monthlyAnalog && (
                    <p>${Number(monthlyAnalog.price).toFixed(0)}/mo</p>
                  )}
                  <div className={cls.Bold}>
                    {selectedInterval === ProductInterval.Yearly && `Billed at `}${Number(productData.price).toFixed(0)}
                    /{selectedInterval === ProductInterval.Monthly ? "mo" : "yr"}
                  </div>
                  {selectedInterval === ProductInterval.Yearly && monthlyAnalog && (
                    <div className={cls.SaveUp}>(Save ${calcAnnualSavings(productData, monthlyAnalog).toFixed(0)})</div>
                  )}
                  {isSelected && <WhiteCheckIcon className={cls.CheckIcon} />}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
